import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Loading,
} from '@startupquickstart/frontend';
import { Map, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type { Tour } from '@/config/objects/tours';

interface TourMultiSelectDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Candidate tours to choose from. Already filtered by caller. */
  tours: Tour[];
  isLoading?: boolean;
  /**
   * Tour ids to hide from the candidate list (already-pinned, already-linked,
   * etc.). Hidden — not just disabled — so the list stays focused on what the
   * user can actually act on.
   */
  excludeIds?: ReadonlySet<string>;
  /**
   * Hard cap on how many additional tours can be selected this session.
   * When set, the submit button stays disabled past the cap and a hint
   * appears in the footer.
   */
  maxSelection?: number;
  /** Final submission. Resolves when all selected tours have been added. */
  onSubmit: (tours: Tour[]) => Promise<void> | void;
  submitLabel?: (count: number) => string;
  emptyMessage?: string;
}

/**
 * Multi-select picker for tours with checkbox rows, thumbnails and client-side
 * search. Used by both the Top Picks list (cap-aware) and the All Tours list
 * (uncapped batch add to a hotel).
 */
export function TourMultiSelectDialog({
  open,
  onClose,
  title,
  description,
  tours,
  isLoading = false,
  excludeIds,
  maxSelection,
  onSubmit,
  submitLabel,
  emptyMessage,
}: TourMultiSelectDialogProps) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time the modal is opened so prior selections never leak.
  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedIds(new Set());
      setSubmitting(false);
    }
  }, [open]);

  const candidates = useMemo(() => {
    const filteredByExclude = excludeIds
      ? tours.filter((tour) => !excludeIds.has(tour.id))
      : tours;
    const term = search.trim().toLowerCase();
    if (!term) return filteredByExclude;
    return filteredByExclude.filter((tour) => {
      const name = tour.name?.toLowerCase() ?? '';
      const type = tour.tourType?.name?.toLowerCase() ?? '';
      return name.includes(term) || type.includes(term);
    });
  }, [tours, excludeIds, search]);

  const selectedCount = selectedIds.size;
  const overCap =
    typeof maxSelection === 'number' && selectedCount > maxSelection;
  const atCap =
    typeof maxSelection === 'number' && selectedCount >= maxSelection;

  // "Select all" tri-state is computed against the *visible* candidates only,
  // so a search filter narrows what the bulk toggle applies to.
  const visibleSelectedCount = candidates.reduce(
    (count, tour) => (selectedIds.has(tour.id) ? count + 1 : count),
    0,
  );
  const allVisibleSelected =
    candidates.length > 0 && visibleSelectedCount === candidates.length;
  const someVisibleSelected =
    visibleSelectedCount > 0 && visibleSelectedCount < candidates.length;
  const selectAllChecked: boolean | 'indeterminate' = allVisibleSelected
    ? true
    : someVisibleSelected
      ? 'indeterminate'
      : false;

  const toggle = (tourId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(tourId)) {
        next.delete(tourId);
      } else {
        next.add(tourId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      // Deselect every visible candidate, leave any out-of-filter picks alone.
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const tour of candidates) next.delete(tour.id);
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const tour of candidates) {
        if (typeof maxSelection === 'number' && next.size >= maxSelection) {
          break;
        }
        next.add(tour.id);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedCount === 0 || overCap) return;
    const selectedTours = candidates.filter((tour) => selectedIds.has(tour.id));
    // Some selected ids may live outside the current search filter — resolve
    // those from the full tours list to be safe.
    const remaining = selectedIds.size - selectedTours.length;
    const finalSelection =
      remaining > 0
        ? tours.filter((tour) => selectedIds.has(tour.id))
        : selectedTours;

    setSubmitting(true);
    try {
      await onSubmit(finalSelection);
    } catch {
      // Caller is expected to surface its own error toast; we just keep the
      // dialog open so the user can retry.
    } finally {
      setSubmitting(false);
    }
  };

  const buttonLabel =
    submitLabel?.(selectedCount) ??
    (selectedCount === 0
      ? 'Add'
      : `Add ${selectedCount} ${selectedCount === 1 ? 'tour' : 'tours'}`);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[80vh] flex-col overflow-hidden p-0 sm:max-w-[600px]">
        <DialogHeader className="px-6 pb-3 pt-6">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tours..."
              className="pl-9"
              autoFocus
            />
          </div>
        </DialogHeader>

        {!isLoading && candidates.length > 0 && (
          <div
            role="button"
            tabIndex={0}
            aria-pressed={allVisibleSelected}
            onClick={toggleSelectAll}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleSelectAll();
              }
            }}
            className="flex w-full cursor-pointer items-center gap-3 border-y bg-muted/30 px-6 py-2 text-sm font-medium hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <Checkbox
              checked={selectAllChecked}
              tabIndex={-1}
              aria-label="Select all visible tours"
            />
            <span className="flex-1">
              {allVisibleSelected ? 'Deselect all' : 'Select all'}
              {search.trim() ? ' matching' : ''}
            </span>
            <span className="text-xs text-muted-foreground">
              {visibleSelectedCount} / {candidates.length}
            </span>
          </div>
        )}

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-6 py-3">
          {isLoading && <Loading text="Loading tours..." size="sm" />}
          {!isLoading && candidates.length === 0 && (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              {search.trim()
                ? `No tours match "${search.trim()}".`
                : emptyMessage ?? 'No tours available.'}
            </div>
          )}
          {candidates.map((tour) => {
            const checked = selectedIds.has(tour.id);
            const disable = !checked && atCap;
            return (
              <div
                key={tour.id}
                role="button"
                tabIndex={disable ? -1 : 0}
                aria-pressed={checked}
                aria-disabled={disable}
                onClick={() => {
                  if (!disable) toggle(tour.id);
                }}
                onKeyDown={(e) => {
                  if (disable) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle(tour.id);
                  }
                }}
                className={`flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  disable
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer hover:bg-accent'
                } ${checked ? 'border-primary bg-accent/40' : ''}`}
              >
                <Checkbox
                  checked={checked}
                  disabled={disable}
                  tabIndex={-1}
                  aria-label={`Select ${tour.name}`}
                />
                {/* Click handling lives on the row, so the Checkbox is purely visual here. */}
                {tour.featuredImage?.url ? (
                  <img
                    src={tour.featuredImage.url}
                    alt={tour.name}
                    className="h-10 w-14 shrink-0 rounded object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded bg-muted">
                    <Map className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {tour.name}
                  </div>
                  {tour.tourType?.name && (
                    <div className="truncate text-xs text-muted-foreground">
                      {tour.tourType.name}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-col items-stretch gap-2 border-t px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-muted-foreground">
            {selectedCount > 0
              ? `${selectedCount} selected${
                  typeof maxSelection === 'number'
                    ? ` of ${maxSelection} max`
                    : ''
                }`
              : typeof maxSelection === 'number'
                ? `Select up to ${maxSelection} ${maxSelection === 1 ? 'tour' : 'tours'}`
                : 'Select one or more tours'}
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={selectedCount === 0 || overCap || submitting}
              loading={submitting}
            >
              {buttonLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
